<template>
  <footer class="w-full">
    <component
      :is="item.component"
      v-for="item in blok?.preContent ?? []"
      :key="item._uid"
      :blok="item"
    />
    <Benefits
      v-show="!hideBenefits && !(isCheckoutPage && showCheckoutPageNewLayout)"
    />
    <div
      v-if="footer && !(isCheckoutPage && showCheckoutPageNewLayout)"
      id="footer-nav"
      class="bg-white text-black"
    >
      <div class="container w-full md:px-4 md:py-[54px] xl:px-20">
        <div
          v-if="footer.groups.length"
          class="md:grid md:grid-cols-6 md:gap-10 lg:grid-cols-12"
        >
          <div
            v-for="(group, groupIndex) in footer.groups"
            :key="groupIndex"
            class="overflow-auto md:col-span-3"
          >
            <section
              v-for="(category, categoryIndex) in group"
              :key="categoryIndex"
              class="p-1 md:mb-2.5"
            >
              <template
                v-if="
                  (category as FooterCategory).items &&
                  (category as FooterCategory).items.length > 0
                "
              >
                <!-- Accordion mode (< md): interactive disclosure toggle. -->
                <Button
                  v-if="
                    footerA11y((category as FooterCategory).key).isInteractive
                  "
                  :id="`accordion-header-${(category as FooterCategory).key}`"
                  type="ghost"
                  ignore-capitalisation
                  :aria-controls="`accordion-content-${(category as FooterCategory).key}`"
                  :aria-expanded="
                    footerA11y((category as FooterCategory).key).ariaExpanded
                  "
                  :class="headerClass"
                  data-test-id="footer-category-toggle"
                  @click="toggleActive((category as FooterCategory).key)"
                >
                  <span
                    class="inline-block overflow-hidden text-ellipsis text-start"
                  >
                    {{ $t((category as FooterCategory).key) }}
                  </span>
                  <IconArrowDown class="accordion-chevron md:hidden" />
                </Button>
                <!-- Static mode (>= md): plain non-interactive label, no expandable semantics. -->
                <div v-else :class="headerClass">
                  <span
                    class="inline-block overflow-hidden text-ellipsis text-start"
                  >
                    {{ $t((category as FooterCategory).key) }}
                  </span>
                </div>
              </template>

              <!--
                Panel visibility is driven solely by the `hidden` attribute (see
                footerA11y / getFooterCategoryA11yState). Do NOT add a `display`
                utility (md:block, hidden, etc.) here — it would override
                [hidden] and desync visual vs. a11y visibility (ECOM-2462).
              -->
              <nav
                :id="`accordion-content-${(category as FooterCategory).key}`"
                :aria-labelledby="
                  footerA11y((category as FooterCategory).key).ariaLabelledby
                "
                :role="footerA11y((category as FooterCategory).key).role"
                :hidden="
                  footerA11y((category as FooterCategory).key).panelHidden
                "
                class="accordion-content px-[15px] pb-2.5 md:p-0 md:px-2"
              >
                <span
                  v-for="(item, itemIndex) in (category as FooterCategory)
                    .items"
                  :key="itemIndex"
                >
                  <nuxt-link
                    v-if="item.to"
                    class="block py-2.5 text-lg font-normal hover:underline md:text-2lg"
                    data-test-id="footer-category-item-link"
                    :to="item.to"
                    :target="item.target"
                    @click="trackNavigationFooter(item.key)"
                  >
                    {{ item.key }}
                  </nuxt-link>
                  <span
                    v-else-if="item.icon"
                    class="mb-[5px] mr-[5px] inline-flex h-[35px]"
                    :title="item.key"
                  >
                    <NuxtLink
                      v-if="item.iconTo"
                      :to="item.iconTo"
                      data-test-id="footer-category-item-with-img-link"
                      target="_blank"
                    >
                      <span class="sr-only">
                        {{ getTargetBlankLabel(item.key, $t) }}
                      </span>
                      <!-- Plain <img>: SVG icons need no responsive srcset (ECOM-2361). -->
                      <img
                        :src="item.icon"
                        :alt="item.key"
                        width="50"
                        height="35"
                        class="h-[35px] w-auto"
                      />
                    </NuxtLink>
                    <!-- Plain <img>: SVG icons need no responsive srcset (ECOM-2361). -->
                    <img
                      v-else
                      :src="item.icon"
                      :alt="item.key"
                      width="50"
                      height="35"
                      class="h-[35px] w-auto"
                    />
                  </span>
                  <span v-else class="block py-1 text-lg md:text-2lg">
                    {{ item.key }}
                  </span>
                </span>
              </nav>
            </section>
            <div
              v-if="blok && groupIndex === footer.groups.length - 1"
              class="flex"
            >
              <div
                class="box-border flex w-full flex-col justify-center gap-1.5 space-y-2 bg-white py-4 text-center text-black md:w-[200px] md:justify-start md:rounded-md md:text-start"
              >
                <p class="text-lg font-bold">
                  {{ blok.contact_info_title }}
                </p>
                <p class="text-base font-normal">
                  {{ blok.contact_info_text }}
                </p>
                <a
                  :href="`tel:${blok.contact_info_phone}`"
                  rel="noopener noreferrer"
                  class="lg:text-base lg:font-bold"
                >
                  {{ blok.contact_info_phone }}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <BottomFooter v-if="blok" :blok="blok" />
  </footer>
</template>

<script setup lang="ts">
import { computed, provide, ref } from 'vue'
import BottomFooter from './BottomFooter.vue'
import Benefits from '~/components/layout/footers/Benefits.vue'
import type {
  SbFooter,
  SbFooterNavigationSection,
  SbFooterInfoItem,
  SbFooterNavigationItem,
} from '~/cms/storyblok/types/storyblok.gen'
import { useRoute } from '#app/composables/router'
import {
  getTargetBlankLabel,
  getFooterCategoryA11yState,
} from '#layers/base/utils/a11y'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'
import { FOOTER_PRE_CONTENT_KEY } from '#layers/base/constants/injectionKeys'

const { trackNavigationFooter } = useTrackingEvents()

type FooterCategory = {
  key: string
  items: {
    key: string
    icon?: string
    iconTo?: string
    to?: string
    target?: string
  }[]
}

type FooterProps = {
  blok?: SbFooter
  hideBenefits?: boolean
}

const props = withDefaults(defineProps<FooterProps>(), {
  blok: undefined,
  hideBenefits: false,
})

// Lets a `preContent` blok tell that it is the footer placement rather than a
// page-body one, so it can defer to a body instance (ECOM-2758).
provide(FOOTER_PRE_CONTENT_KEY, true)

const { showCheckoutPageNewLayout } = useExperiments()
const route = useRoute()
const isCheckoutPage = computed(() => route.fullPath.includes('checkout'))

// `isMd` is true below the md breakpoint (< 768px) — the range where the
// footer is a real accordion. It is useState-backed (SSR value == first client
// render, no hydration mismatch) and corrects to the true width on mount.
const { isMd } = useDeviceType()

// Shared header styling for the interactive toggle (accordion mode) and the
// static label (desktop). Kept identical so the desktop appearance is unchanged
// when the element swaps to a non-interactive label at >= md.
const headerClass =
  'accordion-headline flex w-full max-w-full cursor-pointer items-center justify-between border-t border-white px-[15px] text-lg font-normal leading-6 text-black md:mb-4 md:cursor-auto md:border-0 md:p-0 md:text-xl md:font-bold lg:px-0'

const activeCategory = ref<string | null>(null)

// Single source of truth for the header/panel a11y semantics, consumed
// directly by the template so the DOM matches the unit-tested contract.
const footerA11y = (key: string) =>
  getFooterCategoryA11yState(isMd.value, activeCategory.value === key, key)

const toggleActive = (categoryKey: string) => {
  activeCategory.value =
    activeCategory.value === categoryKey ? null : categoryKey
}

// Safe access to blok without destructuring
const footer = computed(() => {
  const blok = props.blok
  const sections = blok?.sections ?? []
  const payments = blok?.payments ?? []
  const carriers = blok?.carriers ?? []
  const security = blok?.security ?? []

  return {
    groups: [
      ...(sections
        .filter(visiblePredicate)
        .map((section: SbFooterNavigationSection) => [
          {
            ...section,
            key: section.title,
            items:
              section.items?.filter(visiblePredicate).map((item) => ({
                key: item.label,
                to: resolveStoryblokLink({ link: item.link }),
                target: item.link?.target,
              })) ?? [],
          },
        ]) ?? []),

      [
        ...(payments.length
          ? [
              {
                key: 'footer.category.payment.label',
                items: payments.filter(visiblePredicate).map(mapInfoItem),
              },
            ]
          : []),

        ...(carriers.length
          ? [
              {
                key: 'footer.category.carrier.label',
                items: carriers.filter(visiblePredicate).map(mapInfoItem),
              },
            ]
          : []),

        ...(security.length
          ? [
              {
                key: 'footer.category.security.label',
                items: security.filter(visiblePredicate).map(mapInfoItem),
              },
            ]
          : []),
      ],
    ],
  }
})

function mapInfoItem(item: SbFooterInfoItem) {
  return {
    key: item.title,
    icon: item.icon?.filename,
    iconTo: item.link?.cached_url,
  }
}

function visiblePredicate(
  item: SbFooterNavigationItem | SbFooterInfoItem | SbFooterNavigationSection,
) {
  return item.visible
}
</script>

<style lang="postcss" scoped>
.accordion-headline.active > .accordion-chevron {
  transform: rotate(180deg);
}

.footer-column:last-of-type {
  grid-column: 10 / 13;
}
</style>
